"use client";

import React from "react";
import { Check, Star, Shield } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 3,
  order: "asc",
  orderby: "menu_order"
};
const wp_query = new WP_Query(wp_query_params);
function TiersGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mt-16">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="h-[500px] animate-pulse bg-card/50 border-border/50" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center text-destructive mt-16 p-8 bg-destructive/10 rounded-xl border border-destructive/20 max-w-2xl mx-auto">
        <p>Failed to load investment tiers. Please try again later.</p>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="text-center text-muted-foreground mt-16">
        <p>No investment packages are currently available.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mt-16 items-center">
      {products.map((product, i) => {
      const isHighlighted = i === 1;
      return <Card key={product.id} data-index={i} className={`relative flex flex-col h-full transition-all duration-300 ${isHighlighted ? 'border-primary shadow-[0_0_40px_rgba(var(--color-primary),0.15)] md:scale-105 z-10 bg-card' : 'border-border/50 bg-card/80 hover:bg-card hover:border-border'}`}>
            {isHighlighted && <div className="absolute -top-4 left-0 right-0 flex justify-center z-20">
                <Badge className="px-4 py-1.5 text-sm font-bold uppercase tracking-widest bg-primary text-primary-foreground shadow-lg">
                  Most Popular
                </Badge>
              </div>}

            <CardHeader className="text-center pb-4 pt-8">
              <CardTitle className="text-3xl font-bold tracking-tight text-foreground">
                {product.title}
              </CardTitle>
              <CardDescription className="text-muted-foreground mt-2">
                Private Round Allocation
              </CardDescription>
            </CardHeader>

            <CardContent className="flex-grow flex flex-col items-center pt-2 px-6 md:px-8">
              <div className="mb-8 text-center w-full pb-8 border-b border-border/50">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-extrabold text-foreground">${product.price}</span>
                  <span className="text-lg text-muted-foreground font-medium">/ token</span>
                </div>
                {product.discounted_price && <div className="mt-2 text-sm text-primary font-medium">
                    Early bird discount applied
                  </div>}
              </div>

              <div className="w-full space-y-5 text-sm md:text-base">
                {}
                {product.customFields?.lock_up_period && <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="text-muted-foreground block text-xs uppercase tracking-wider font-semibold mb-1">Lock-up Period</span>
                      <strong className="text-foreground font-medium">{product.customFields.lock_up_period}</strong>
                    </div>
                  </div>}
                
                {product.customFields?.bonus_percentage && <div className="flex items-start gap-3">
                    <Star className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="text-muted-foreground block text-xs uppercase tracking-wider font-semibold mb-1">Bonus Allocation</span>
                      <strong className="text-foreground font-medium">{product.customFields.bonus_percentage}</strong>
                    </div>
                  </div>}

                {}
                {product.short_description && <div className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground
                               [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0 [&_ul]:space-y-3
                               [&_li]:flex [&_li]:items-start [&_li]:gap-3 [&_li]:m-0
                               [&_li::before]:content-[''] [&_li::before]:text-primary [&_li::before]:font-bold [&_li::before]:text-lg [&_li::before]:leading-none [&_li::before]:mt-0.5" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} />}
              </div>
            </CardContent>

            <CardFooter className="pt-8 pb-8 px-6 md:px-8 mt-auto">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName={`w-full py-6 text-lg font-bold transition-all ${isHighlighted ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`} text="Select Package" showQuantity={false} />
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function InvestmentTiers() {
  return <section id="investment-tiers" className="dark bg-background text-foreground py-24 md:py-32 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[600px] bg-primary/5 blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-secondary/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
          <Badge variant="outline" className="mb-6 border-primary/30 text-primary bg-primary/5 px-4 py-1.5 text-sm tracking-widest uppercase">
            Private Sale Open
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight text-foreground">
            Strategic <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/70">Investment Tiers</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Secure your position in the AizaHcare ecosystem. Choose the investment package that aligns with your strategic goals and participate in the future of AI-powered healthcare infrastructure.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 3,
        order: "asc",
        orderby: "menu_order"
      }}>
          <TiersGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}